import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@cmg/design-system';
import { FormattedNumber, useIntl } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';

import { useConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import { SecurityType } from '../../../../../../../../graphql';
import { interestUnitLabels } from '../../../../../../../../types/domain/order-book/constants';
import { SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment } from '../../../../context/hooks/syndicate/__generated__';
import { IndicationForm_IndicationPartsFragment } from '../../../indication-form/graphql/__generated__';
import { getConvertibleOrderType } from '../utils/getConvertibleOrderType';
import { getInterestQuantityCellValue } from './IndicationSectionReadContent.model';

export type InterestLevelsTableReadContentConvertiblesProps = Readonly<{
  interestLevels: IndicationForm_IndicationPartsFragment['interestLevels'];
  filings: Readonly<SyndicateIndication_PublishedOfferings_Terms_FilingsPartsFragment[]> | null;
  showPercentageOfPar: boolean;
  securityType: SecurityType | null;
}>;

export function InterestLevelsTableReadContentConvertibles({
  interestLevels,
  filings,
  showPercentageOfPar,
}: InterestLevelsTableReadContentConvertiblesProps) {
  const intl = useIntl();
  const convertibleFilingTalkRanges = useConvertibleFilingTalkRanges(filings);

  return (
    <Stack>
      <TableContainer>
        <Table sx={{ width: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell align="right">Demand Quantity</TableCell>
              <TableCell>Demand Unit</TableCell>
              <TableCell>Order Type</TableCell>
              <TableCell align="right">Coupon</TableCell>
              <TableCell align="right">Premium</TableCell>
              <TableCell align="right">% of Par</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {interestLevels.map(
              ({ interestQuantity, interestUnit, coupon, premium, percentageOfPar }, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell align="right">
                      {getInterestQuantityCellValue({ interestUnit, interestQuantity, intl })}
                    </TableCell>
                    <TableCell>{interestUnitLabels[interestUnit]}</TableCell>
                    <TableCell>
                      {getConvertibleOrderType({
                        coupon: defaultTo(coupon, null),
                        premium: defaultTo(premium, null),
                        percentageOfPar: defaultTo(percentageOfPar, null),
                        isIndicationPriceAsPercentageParEnabled: showPercentageOfPar,
                        couponTalkLow: convertibleFilingTalkRanges?.couponTalkLow!,
                        couponTalkHigh: convertibleFilingTalkRanges?.couponTalkHigh!,
                        premiumTalkLow: convertibleFilingTalkRanges?.premiumTalkLow!,
                        premiumTalkHigh: convertibleFilingTalkRanges?.premiumTalkHigh!,
                        priceAsOfPar: convertibleFilingTalkRanges?.priceAsOfPar!,
                      })}
                    </TableCell>
                    <TableCell align="right">
                      <FormattedNumber
                        value={coupon}
                        format="percent"
                        maximumFractionDigits={4}
                        minimumFractionDigits={4}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <FormattedNumber value={premium} format="percent" />
                    </TableCell>
                    <TableCell align="right">
                      <FormattedNumber value={percentageOfPar} format="percent" />
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
